import {
  ButtonProps,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  MenuButtonProps,
} from '@chakra-ui/react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { StrictFieldValues, StringPath } from './types';
import { Select } from 'packages/common-frontend/components/Select';

export type SelectInputProps<T extends StrictFieldValues> = {
  name: StringPath<T>;
  label?: string;
  // options can hold arbitrary properties, but label and value must be present
  options: { label: React.ReactNode; value: string; [key: string]: any }[];
  placeholder?: string;
  helperText?: React.ReactNode;
  required?: boolean;
  isDisabled?: boolean;
  tag?: React.ReactNode;
  inputProps?: MenuButtonProps & ButtonProps;
};
export const SelectInput = <T extends StrictFieldValues>({
  name,
  label,
  options,
  placeholder = '',
  helperText = '',
  required,
  isDisabled = false,
  tag,
  inputProps,
}: SelectInputProps<T>) => {
  const { control } = useFormContext<T>();
  const rules = required
    ? {
        required: { value: true, message: 'This is a required field' },
      }
    : undefined;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        return (
          <FormControl isInvalid={!!error}>
            <Flex alignItems="center">
              {label && <FormLabel fontWeight="bold">{label}</FormLabel>}
              {tag}
            </Flex>
            <Select
              isDisabled={isDisabled}
              placeholderLabel={placeholder}
              isInvalid={!!error}
              // Can cast to string here because StringPath ensures that this field's
              // value is a string
              value={field.value as string}
              onChange={(nextValue) => {
                field.onChange(nextValue);
              }}
              options={options}
              onClose={field.onBlur}
              showEmpty
              buttonProps={inputProps}
            />
            <FormErrorMessage>{error?.message}</FormErrorMessage>
            <FormHelperText>{helperText}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
};
